/*
  Haishen modern layer — behaviour for haishen-modern.css.

  - scroll reveals (only for elements that start off-screen, so nothing that
    is already painted ever blinks out)
  - header shadow once the page scrolls
  - back-to-top button with a scroll-progress ring
  - count-up numbers, constant-speed trust marquee
  - sticky add-to-cart bar on product pages
  - tap-to-call / mail / map links and location cards in the footer contact text
  - cart bubble "pop" and lazy-image shimmer hand-off

  Motion is skipped entirely under prefers-reduced-motion, in the theme editor
  and with ?noanim in the URL.
*/
(function () {
  'use strict';

  var root = document.documentElement;
  var params = new URLSearchParams(window.location.search);
  var noAnim = params.has('noanim');
  var reduceQuery = window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)') : { matches: false };
  var designMode = !!(window.Shopify && window.Shopify.designMode);

  if (noAnim) root.classList.add('hs-noanim');

  function motionAllowed() {
    return !noAnim && !reduceQuery.matches && !designMode && 'IntersectionObserver' in window;
  }

  function onReady(fn) {
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', fn);
    else fn();
  }

  var strings = window.haishenStrings || {};

  /* ------------------------------------------------------------ reveal */

  var REVEAL_RULES = [
    ['.title-wrapper-with-link', 'up'],
    ['.hs-head', 'up'],
    ['.product-grid > .grid__item', 'up'],
    ['.collection-list > .grid__item', 'up'],
    ['.multicolumn-list > .multicolumn-list__item', 'up'],
    ['.blog__posts .article-card-wrapper, .blog-articles > *', 'up'],
    ['.rich-text__blocks > *', 'up'],
    ['.image-with-text__media', 'left'],
    ['.image-with-text__content > *', 'up'],
    ['.video-section .deferred-media', 'zoom'],
    ['.video-section .title-wrapper, .video-section .title', 'up'],
    ['.slideshow', 'fade'],
    ['.hs-spotlight__media', 'left'],
    ['.hs-spotlight__info > *', 'up'],
    ['.hs-stats__head > *', 'up'],
    ['.hs-stat', 'up'],
    ['.collection__view-all', 'up'],
    ['.footer-block', 'up'],
    ['.banner:not(.hs-hero .banner) .banner__box', 'up'],
    ['.product-recommendations .title, .related-products__heading', 'up'],
    ['.collapsible-content__item, .accordion', 'up']
  ];

  var revealObserver = null;

  function markOffscreen(scope) {
    if (!revealObserver) return;
    var vh = window.innerHeight || root.clientHeight;
    var vw = window.innerWidth || root.clientWidth;
    REVEAL_RULES.forEach(function (rule) {
      var nodes = scope.querySelectorAll(rule[0]);
      for (var i = 0; i < nodes.length; i++) {
        var el = nodes[i];
        if (el.hasAttribute('data-hs-reveal') || el.hasAttribute('data-hs-revealed')) continue;
        if (el.closest('[data-hs-reveal]')) continue; // an ancestor already animates
        var rect = el.getBoundingClientRect();
        if (rect.width === 0 && rect.height === 0) continue; // hidden (drawers, tabs)
        // Leave anything already on screen alone: it has been painted.
        var offscreen = rect.top > vh * 0.95 || rect.bottom < 0;
        if (!offscreen) continue;
        // Carousel slides parked off to the side arrive by swiping, not by
        // scrolling — rising in mid-swipe looks broken, so they stay put.
        if (el.closest('.slider') && (rect.left >= vw - 1 || rect.right <= 1)) continue;
        el.setAttribute('data-hs-reveal', rule[1]);
        revealObserver.observe(el);
      }
    });
  }

  function settle(el) {
    el.classList.add('is-settled');
    // Drop the attribute once finished so the element is exactly its stock self.
    window.setTimeout(function () {
      el.removeAttribute('data-hs-reveal');
      el.classList.remove('is-inview', 'is-settled');
      el.style.removeProperty('--hs-i');
      el.setAttribute('data-hs-revealed', '');
    }, 1400);
  }

  function initReveal() {
    if (!motionAllowed()) return;
    revealObserver = new IntersectionObserver(
      function (entries) {
        var shown = entries
          .filter(function (e) {
            return e.isIntersecting;
          })
          .map(function (e) {
            return e.target;
          });
        // Stagger whatever arrives together, in reading order.
        shown.sort(function (a, b) {
          var ra = a.getBoundingClientRect();
          var rb = b.getBoundingClientRect();
          return Math.round(ra.top - rb.top) || ra.left - rb.left;
        });
        shown.forEach(function (el, i) {
          el.style.setProperty('--hs-i', Math.min(i, 7));
          el.classList.add('is-inview');
          revealObserver.unobserve(el);
          settle(el);
        });
      },
      { rootMargin: '0px 0px -6% 0px', threshold: 0.08 }
    );
    markOffscreen(document);
    window.addEventListener('beforeprint', revealAll);
  }

  function revealAll() {
    var nodes = document.querySelectorAll('[data-hs-reveal]');
    for (var i = 0; i < nodes.length; i++) {
      nodes[i].classList.add('is-inview');
      settle(nodes[i]);
    }
  }

  /* ---------------------------------------------- header + back-to-top */

  var topButton = null;
  var topRing = null;
  var RING = 2 * Math.PI * 22;

  function buildTopButton() {
    topButton = document.createElement('button');
    topButton.type = 'button';
    topButton.className = 'hs-top';
    topButton.setAttribute('aria-label', strings.backToTop || 'Back to top');
    topButton.innerHTML =
      '<svg viewBox="0 0 48 48" aria-hidden="true"><circle class="hs-top__ring" cx="24" cy="24" r="22" stroke-dasharray="' +
      RING.toFixed(2) +
      '" stroke-dashoffset="' +
      RING.toFixed(2) +
      '"></circle></svg>' +
      '<svg class="hs-top__arrow" viewBox="0 0 16 16" aria-hidden="true" style="position:relative;transform:none"><path d="M8 13V3M3.5 7.5 8 3l4.5 4.5" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg>';
    topRing = topButton.querySelector('.hs-top__ring');
    topButton.addEventListener('click', function () {
      window.scrollTo({ top: 0, behavior: reduceQuery.matches || noAnim ? 'auto' : 'smooth' });
    });
    document.body.appendChild(topButton);
  }

  function onScrollFrame() {
    var y = window.pageYOffset || root.scrollTop;
    root.classList.toggle('hs-scrolled', y > 24);
    if (!topButton) return;
    var max = Math.max(1, root.scrollHeight - window.innerHeight);
    topButton.classList.toggle('is-visible', y > 700);
    topRing.style.strokeDashoffset = (RING * (1 - Math.min(1, y / max))).toFixed(2);
  }

  function initScroll() {
    buildTopButton();
    var queued = false;
    window.addEventListener(
      'scroll',
      function () {
        if (queued) return;
        queued = true;
        window.requestAnimationFrame(function () {
          queued = false;
          onScrollFrame();
        });
      },
      { passive: true }
    );
    onScrollFrame();
  }

  /* --------------------------------------------------------- count-up */

  function runCount(el) {
    var target = parseFloat(el.getAttribute('data-hs-count'));
    if (!isFinite(target)) return;
    var decimals = (el.getAttribute('data-hs-count').split('.')[1] || '').length;
    if (!motionAllowed()) {
      el.textContent = target.toFixed(decimals);
      return;
    }
    var start = null;
    var duration = 1600;
    function frame(ts) {
      if (start === null) start = ts;
      var t = Math.min(1, (ts - start) / duration);
      var eased = 1 - Math.pow(1 - t, 3);
      el.textContent = (target * eased).toFixed(decimals);
      if (t < 1) window.requestAnimationFrame(frame);
    }
    el.textContent = (0).toFixed(decimals);
    window.requestAnimationFrame(frame);
  }

  function initCounters(scope) {
    if (!motionAllowed()) return; // the markup already holds the final figures
    var nodes = scope.querySelectorAll('[data-hs-count]:not([data-hs-counted])');
    if (!nodes.length) return;
    var io = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (e) {
          if (!e.isIntersecting) return;
          io.unobserve(e.target);
          e.target.setAttribute('data-hs-counted', '');
          runCount(e.target);
        });
      },
      { threshold: 0.6 }
    );
    for (var i = 0; i < nodes.length; i++) {
      // Only count what the visitor will actually watch arrive.
      if (nodes[i].getBoundingClientRect().top < window.innerHeight * 0.9) {
        nodes[i].setAttribute('data-hs-counted', '');
        continue;
      }
      // Start from zero while still off-screen, so the count never jumps back.
      var decimals = (nodes[i].getAttribute('data-hs-count').split('.')[1] || '').length;
      nodes[i].textContent = (0).toFixed(decimals);
      io.observe(nodes[i]);
    }
  }

  /* ---------------------------------------------------------- marquee */

  function initMarquees(scope) {
    var marquees = scope.querySelectorAll('.hs-marquee');
    for (var i = 0; i < marquees.length; i++) {
      var m = marquees[i];
      var track = m.querySelector('.hs-marquee__track');
      var lists = m.querySelectorAll('.hs-marquee__list');
      if (!track || lists.length < 2) continue;
      var first = lists[0];
      var clone = lists[1];
      // Each half must be at least as wide as the viewport or the loop shows a gap.
      var guard = 0;
      var originals = Array.prototype.slice.call(first.children);
      while (first.scrollWidth < m.clientWidth && originals.length && guard < 12) {
        originals.forEach(function (item) {
          var copy = item.cloneNode(true);
          copy.classList.add('hs-marquee__item--dup');
          copy.setAttribute('aria-hidden', 'true');
          first.appendChild(copy);
        });
        guard++;
      }
      clone.innerHTML = first.innerHTML;
      var speed = parseFloat(m.getAttribute('data-speed')) || 45; // px per second
      track.style.setProperty('--hs-marquee-duration', (first.scrollWidth / speed).toFixed(1) + 's');
    }
  }

  /* -------------------------------------------- cart bubble + images */

  function initCartPop() {
    var bubble = document.getElementById('cart-icon-bubble');
    if (!bubble || !('MutationObserver' in window)) return;
    var last = (bubble.textContent || '').trim();
    new MutationObserver(function () {
      var now = (bubble.textContent || '').trim();
      if (now === last) return;
      last = now;
      var count = bubble.querySelector('.cart-count-bubble');
      if (!count || !motionAllowed()) return;
      count.classList.remove('hs-pop');
      void count.offsetWidth;
      count.classList.add('hs-pop');
    }).observe(bubble, { childList: true, subtree: true, characterData: true });
  }

  // Dawn's "added to cart" popup lives inside the header, which hides itself
  // while scrolling down — slide the header back in whenever the popup opens
  // (e.g. after adding from the sticky bar deep in a product page).
  function initCartNotificationReveal() {
    var note = document.getElementById('cart-notification');
    var sticky = document.querySelector('sticky-header');
    if (!note || !sticky || typeof sticky.reveal !== 'function' || !('MutationObserver' in window)) return;
    new MutationObserver(function () {
      var header = document.getElementById('shopify-section-header');
      if (note.classList.contains('active') && header && header.classList.contains('shopify-section-header-hidden')) {
        sticky.reveal();
      }
    }).observe(note, { attributes: true, attributeFilter: ['class'] });
  }

  function markLoaded(img) {
    var media = img.closest('.hs-card .media');
    if (media) media.classList.add('hs-loaded');
  }

  function initImages(scope) {
    var imgs = scope.querySelectorAll('.hs-card .media img');
    for (var i = 0; i < imgs.length; i++) {
      if (imgs[i].complete) markLoaded(imgs[i]);
    }
  }

  document.addEventListener(
    'load',
    function (e) {
      if (e.target && e.target.tagName === 'IMG') markLoaded(e.target);
    },
    true
  );
  document.addEventListener(
    'error',
    function (e) {
      if (e.target && e.target.tagName === 'IMG') markLoaded(e.target);
    },
    true
  );

  /* ---------------------------------------------- footer contact text */

  var NAME_LINE = /[:：]\s*$/;
  var PHONE = /(\+?1[\s.-]?)?\(?(\d{3})\)?[\s.-]?(\d{3,4})[\s.-]?(\d{4})\b/g;
  var EMAIL = /[\w.+-]+@[\w-]+(?:\.[\w-]+)+/g;
  var ADDRESS = /^(地址|Address)\s*[:：]\s*/i;

  function telHref(match) {
    var digits = match.replace(/\D/g, '');
    // "1-604-…" / "+1 604 …" carry the North American country code; a bare
    // 11-digit 1[3-9]… number (186-0134-4086) is a mainland China mobile.
    var hasCountryCode = /^\s*\+/.test(match) || /^\s*1[\s.-]/.test(match);
    if (digits.length === 11 && /^1[3-9]\d{9}$/.test(digits) && !hasCountryCode) return 'tel:+86' + digits;
    if (digits.length === 10) return 'tel:+1' + digits;
    if (digits.length === 11 && digits.charAt(0) === '1') return 'tel:+' + digits;
    return 'tel:' + digits;
  }

  function linkifyTextNode(node) {
    var text = node.nodeValue;
    var parts = [];
    var re = new RegExp(PHONE.source + '|' + EMAIL.source, 'g');
    var last = 0;
    var m;
    while ((m = re.exec(text))) {
      var hit = m[0];
      var isEmail = hit.indexOf('@') !== -1;
      var digits = hit.replace(/\D/g, '');
      if (!isEmail && digits.length < 10) continue;
      if (m.index > last) parts.push(document.createTextNode(text.slice(last, m.index)));
      var a = document.createElement('a');
      a.className = 'hs-contact-link';
      a.href = isEmail ? 'mailto:' + hit : telHref(hit);
      a.textContent = hit;
      parts.push(a);
      last = m.index + hit.length;
    }
    if (!parts.length) return;
    if (last < text.length) parts.push(document.createTextNode(text.slice(last)));
    var frag = document.createDocumentFragment();
    parts.forEach(function (p) {
      frag.appendChild(p);
    });
    node.parentNode.replaceChild(frag, node);
  }

  function linkify(container) {
    var walker = document.createTreeWalker(container, NodeFilter.SHOW_TEXT, null);
    var nodes = [];
    while (walker.nextNode()) {
      if (!walker.currentNode.parentNode.closest('a')) nodes.push(walker.currentNode);
    }
    nodes.forEach(linkifyTextNode);
  }

  function mapAddresses(container) {
    var ps = container.querySelectorAll('p');
    for (var i = 0; i < ps.length; i++) {
      var p = ps[i];
      if (p.querySelector('a')) continue;
      var text = (p.textContent || '').trim();
      if (!ADDRESS.test(text)) continue;
      var address = text.replace(ADDRESS, '').trim();
      if (address.length < 8) continue;
      var label = text.slice(0, text.length - address.length);
      p.textContent = label;
      var a = document.createElement('a');
      a.className = 'hs-contact-link';
      a.href = 'https://www.google.com/maps/search/?api=1&query=' + encodeURIComponent(address);
      a.target = '_blank';
      a.rel = 'noopener';
      a.textContent = address;
      p.appendChild(a);
    }
  }

  function groupLocations(container) {
    var paras = Array.prototype.filter.call(container.children, function (el) {
      return el.tagName === 'P';
    });
    var names = paras.filter(function (p) {
      var t = (p.textContent || '').replace(/​/g, '').trim();
      return t.length > 1 && t.length <= 24 && NAME_LINE.test(t) && !/\d{3}/.test(t);
    });
    if (names.length < 2) return;
    var wrap = document.createElement('div');
    wrap.className = 'hs-locations';
    var card = null;
    paras.forEach(function (p) {
      var t = (p.textContent || '').replace(/​/g, '').trim();
      if (names.indexOf(p) !== -1) {
        card = document.createElement('div');
        card.className = 'hs-loc';
        p.classList.add('hs-loc__name');
        p.textContent = t.replace(NAME_LINE, '');
        wrap.appendChild(card);
      }
      if (!t) {
        p.remove();
        return;
      }
      if (card) card.appendChild(p);
    });
    container.appendChild(wrap);

    var block = container.closest('.footer-block');
    var wrapper = block && block.parentElement;
    if (block) block.classList.add('hs-footer-contact');
    if (wrapper && wrapper.children.length <= 4) wrapper.classList.add('hs-has-contact');
  }

  function initFooter() {
    var blocks = document.querySelectorAll('.footer .footer-block__details-content.rte');
    for (var i = 0; i < blocks.length; i++) {
      mapAddresses(blocks[i]);
      linkify(blocks[i]);
      groupLocations(blocks[i]);
    }
  }

  /* ------------------------------------------------ sticky add-to-cart */

  function priceText(scope) {
    var price = scope && scope.querySelector('.price');
    if (!price) return '';
    var sale = price.classList.contains('price--on-sale') && price.querySelector('.price__sale .price-item--sale');
    var regular = price.querySelector('.price__regular .price-item--regular');
    var el = sale || regular;
    return el ? el.textContent.replace(/\s+/g, ' ').trim() : '';
  }

  function initStickyAtc() {
    var product = document.querySelector('#MainContent .product');
    if (!product) return;
    var form = product.querySelector('product-form form');
    var submit = form && form.querySelector('button[type="submit"][name="add"]');
    if (!submit) return;

    var titleEl = product.querySelector('.product__title');
    var priceScope = product.querySelector('[id^="price-"]') || product.querySelector('.product__info-container');
    var thumbEl = product.querySelector('.product__media img');

    var bar = document.createElement('div');
    bar.className = 'hs-atc';
    bar.setAttribute('role', 'region');
    bar.setAttribute('aria-label', strings.stickyCart || 'Add to cart');
    bar.innerHTML =
      (thumbEl ? '<img class="hs-atc__thumb" alt="" loading="lazy">' : '') +
      '<div class="hs-atc__info"><span class="hs-atc__title"></span><span class="hs-atc__price"></span></div>' +
      '<button type="button" class="button hs-atc__button"></button>';
    var barTitle = bar.querySelector('.hs-atc__title');
    var barPrice = bar.querySelector('.hs-atc__price');
    var barButton = bar.querySelector('.hs-atc__button');
    var barThumb = bar.querySelector('.hs-atc__thumb');

    barTitle.textContent = titleEl ? titleEl.textContent.trim() : document.title;
    if (barThumb && thumbEl) {
      var src = thumbEl.currentSrc || thumbEl.src;
      barThumb.src = src;
    }

    function sync() {
      var label = submit.querySelector('span');
      barButton.textContent = ((label || submit).textContent || '').trim();
      barButton.disabled = submit.disabled || submit.hasAttribute('disabled');
      barButton.setAttribute('aria-disabled', barButton.disabled ? 'true' : 'false');
      barPrice.textContent = priceText(priceScope);
    }
    sync();

    barButton.addEventListener('click', function () {
      if (submit.disabled) return;
      submit.click();
    });

    if ('MutationObserver' in window) {
      var mo = new MutationObserver(sync);
      mo.observe(submit, { attributes: true, childList: true, subtree: true, characterData: true });
      if (priceScope) mo.observe(priceScope, { childList: true, subtree: true, characterData: true });
    }

    document.body.appendChild(bar);

    var submitVisible = true;
    var footerVisible = false;
    function update() {
      var scrolled = (window.pageYOffset || root.scrollTop) > 320;
      var show = !submitVisible && !footerVisible && scrolled;
      bar.classList.toggle('is-visible', show);
      document.body.classList.toggle('hs-atc-open', show);
    }
    if ('IntersectionObserver' in window) {
      new IntersectionObserver(function (entries) {
        submitVisible = entries[0].isIntersecting;
        update();
      }).observe(submit);
      var footer = document.querySelector('.footer');
      if (footer) {
        new IntersectionObserver(function (entries) {
          footerVisible = entries[0].isIntersecting;
          update();
        }).observe(footer);
      }
      window.addEventListener('scroll', update, { passive: true });
    }
  }

  /* ------------------------------------------- theme-preview bar offset */

  // Shopify docks a 68px "Exit preview / Copy link" bar to the bottom of
  // preview links and nudges its own chat bubble above it. Lift our fixed
  // bottom UI (and the corner app widgets we position) by the same amount so
  // a preview looks like the published site. The bar never exists on the live
  // storefront, so there the offset stays 0.
  function initPreviewBarOffset() {
    var tries = 0;
    function track(bar) {
      function update() {
        var r = bar.getBoundingClientRect();
        var shown = getComputedStyle(bar).display !== 'none' && r.height > 0 ? Math.max(0, Math.round(window.innerHeight - r.top)) : 0;
        root.style.setProperty('--hs-bottom-offset', Math.min(shown, 120) + 'px');
      }
      update();
      window.setInterval(update, 1000); // "Hide bar" slides it away without resizing it
      window.addEventListener('resize', update);
    }
    (function find() {
      var bar = document.getElementById('PBarNextFrameWrapper');
      if (bar) return track(bar);
      if (++tries < 30) window.setTimeout(find, 500);
    })();
  }

  /* -------------------------------------------------------------- boot */

  onReady(function () {
    initScroll();
    initReveal();
    initCounters(document);
    initMarquees(document);
    initCartPop();
    initCartNotificationReveal();
    initImages(document);
    initFooter();
    initStickyAtc();
    initPreviewBarOffset();
  });

  // Collection filtering / sorting swaps the product grid in place.
  document.addEventListener('shopify:section:load', function (e) {
    initCounters(e.target);
    initMarquees(e.target);
    initImages(e.target);
  });

  if ('MutationObserver' in window) {
    onReady(function () {
      var grid = document.getElementById('ProductGridContainer') || document.querySelector('.collection');
      if (!grid) return;
      new MutationObserver(function () {
        initImages(grid);
        if (revealObserver) markOffscreen(grid);
      }).observe(grid, { childList: true, subtree: true });
    });
  }
})();
